import { Link } from "react-router-dom";
import logo from "@/assets/logo-white.png";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import { PageTransition } from "@/components/PageTransition";

const TermsConditions = () => {
  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        <main className="py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="prose prose-invert mx-auto max-w-3xl">
              <h1 className="mb-2 text-4xl font-bold">Terms & Conditions</h1>
              <p className="mb-8 text-muted-foreground">Last updated: February 2026</p>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">1. Definitions</h2>
                <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>
                    <strong>Platform</strong> – The PROduce web application and mobile application,
                    accessible at produce-app.com and via the PROduce mobile app.
                  </li>
                  <li>
                    <strong>User</strong> – Any person who creates an account or uses the Platform.
                  </li>
                  <li>
                    <strong>Producer</strong> – A User with a paid subscription who creates and
                    manages productions.
                  </li>
                  <li>
                    <strong>Crew member</strong> – A User invited by a Producer to participate in a
                    production.
                  </li>
                  <li>
                    <strong>Production</strong> – A festival or event project created within the
                    Platform.
                  </li>
                  <li>
                    <strong>We / Us / Our</strong> – PROduce, the operator of the Platform.
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">2. Applicability</h2>
                <p className="leading-relaxed text-muted-foreground">
                  These Terms & Conditions apply to all use of the Platform. By creating an account
                  or using the Platform, you agree to be bound by these terms. If you do not agree,
                  you may not use the Platform.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">3. Accounts & Registration</h2>
                <p className="mb-4 leading-relaxed text-muted-foreground">
                  To use the Platform, you must create an account with a valid email address. You
                  are responsible for:
                </p>
                <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>Providing accurate and up-to-date information</li>
                  <li>Keeping your login credentials confidential</li>
                  <li>All activity that occurs under your account</li>
                  <li>Notifying us immediately of any unauthorized use of your account</li>
                </ul>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  We reserve the right to suspend or terminate accounts that violate these terms or
                  are used for fraudulent purposes.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">4. Subscriptions & Payment</h2>
                <p className="mb-4 leading-relaxed text-muted-foreground">
                  The Platform offers free Crew member accounts and paid Producer subscriptions. For
                  paid subscriptions:
                </p>
                <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>
                    Prices are displayed on the Platform and are subject to change with prior notice
                  </li>
                  <li>Payments are processed securely via Stripe</li>
                  <li>
                    Subscriptions renew automatically at the end of each billing period (monthly or
                    yearly)
                  </li>
                  <li>
                    You may cancel your subscription at any time; access continues until the end of
                    the current billing period
                  </li>
                  <li>
                    Trial periods, if offered, are limited to one per user. Creating multiple
                    accounts to obtain additional trials is prohibited
                  </li>
                  <li>
                    Refunds are handled on a case-by-case basis; contact us at
                    support@produce-app.com
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">5. Acceptable Use</h2>
                <p className="mb-4 leading-relaxed text-muted-foreground">
                  You agree to use the Platform only for its intended purpose: managing festival and
                  event productions. You may not:
                </p>
                <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>Use the Platform for any unlawful purpose</li>
                  <li>Attempt to gain unauthorized access to the Platform or its systems</li>
                  <li>Interfere with the proper functioning of the Platform</li>
                  <li>Upload malicious content or software</li>
                  <li>Scrape, copy, or redistribute Platform content without permission</li>
                  <li>Share your account credentials with third parties</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">6. Intellectual Property</h2>
                <p className="mb-4 leading-relaxed text-muted-foreground">
                  The Platform, including its design, code, features, and branding, is the
                  intellectual property of PROduce. You may not copy, modify, or redistribute any
                  part of the Platform.
                </p>
                <p className="leading-relaxed text-muted-foreground">
                  Content you create within the Platform (productions, briefings, schedules) remains
                  your property. By using the Platform, you grant us a limited license to store,
                  process, and display your content solely for the purpose of providing the service.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">7. Crew member access</h2>
                <p className="mb-4 leading-relaxed text-muted-foreground">
                  Producers can invite crew members to their productions. By inviting crew members:
                </p>
                <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>
                    The Producer is responsible for ensuring crew members are aware of the data
                    shared with them
                  </li>
                  <li>
                    Crew members receive access to briefing details, schedules, and assignments
                    relevant to their role
                  </li>
                  <li>
                    Crew members may optionally enable GPS location sharing during productions
                  </li>
                  <li>
                    The Producer is responsible for informing crew members about location tracking
                    before enabling it
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">8. GPS & Location Data</h2>
                <p className="mb-4 leading-relaxed text-muted-foreground">
                  The Platform offers optional GPS tracking for crew coordination during
                  productions. By enabling this feature:
                </p>
                <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>
                    Location data is only collected during active productions and within defined
                    festival boundaries (geofence)
                  </li>
                  <li>Crew members must explicitly consent to location tracking</li>
                  <li>Location data is shared only with the production's Producer</li>
                  <li>Crew members can disable tracking at any time in their settings</li>
                  <li>
                    Location data is not retained indefinitely and is deleted after the production
                    ends
                  </li>
                </ul>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  For more details, see our{" "}
                  <Link to="/privacy-policy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">9. Limitation of Liability</h2>
                <p className="mb-4 leading-relaxed text-muted-foreground">
                  The Platform is provided "as is" and "as available." While we strive to maintain
                  high uptime and reliability, we do not guarantee uninterrupted or error-free
                  service.
                </p>
                <p className="mb-4 leading-relaxed text-muted-foreground">
                  To the maximum extent permitted by law, PROduce shall not be liable for:
                </p>
                <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>
                    Indirect, incidental, or consequential damages arising from your use of the
                    Platform
                  </li>
                  <li>Loss of data, revenue, or profits</li>
                  <li>Interruptions or downtime of the Platform</li>
                  <li>
                    Actions or omissions of third-party service providers (e.g., payment processors,
                    hosting providers)
                  </li>
                </ul>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  Our total liability for any claim related to the Platform shall not exceed the
                  amount you have paid us in the 12 months preceding the claim.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">10. Termination</h2>
                <p className="mb-4 leading-relaxed text-muted-foreground">
                  You may terminate your account at any time through your account settings or by
                  contacting us. Upon termination:
                </p>
                <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
                  <li>Your access to the Platform will be revoked</li>
                  <li>
                    Your production data may be retained for up to 12 months for legal and
                    operational purposes, after which it will be deleted
                  </li>
                  <li>
                    Active subscriptions will not be refunded for the remaining billing period
                    unless required by law
                  </li>
                </ul>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  We reserve the right to terminate or suspend your account if you violate these
                  terms, with or without prior notice.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">11. Changes to These Terms</h2>
                <p className="leading-relaxed text-muted-foreground">
                  We may update these Terms & Conditions from time to time. We will notify you of
                  material changes by posting the updated terms on this page and updating the "Last
                  updated" date. Continued use of the Platform after changes constitutes acceptance
                  of the revised terms.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">12. Contact</h2>
                <p className="leading-relaxed text-muted-foreground">
                  If you have questions about these Terms & Conditions, please contact us:
                </p>
                <ul className="mt-4 list-none space-y-1 text-muted-foreground">
                  <li>
                    Email:{" "}
                    <a
                      href="mailto:support@produce-app.com"
                      className="text-primary hover:underline"
                    >
                      support@produce-app.com
                    </a>
                  </li>
                  <li>
                    Contact form:{" "}
                    <Link to="/contact" className="text-primary hover:underline">
                      produce-app.com/contact
                    </Link>
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="mb-4 text-2xl font-semibold">13. Governing Law</h2>
                <p className="leading-relaxed text-muted-foreground">
                  These Terms & Conditions are governed by and construed in accordance with the laws
                  of the Netherlands. Any disputes arising from or relating to these terms shall be
                  submitted to the competent court in the Netherlands.
                </p>
              </section>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-border py-12" style={{ backgroundColor: "#050e22" }}>
          <div className="container mx-auto px-6">
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              <img src={logo} alt="PROduce Logo" className="h-8" />
              <div className="flex items-center gap-6">
                <Link
                  to="/user-cases"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Use Cases
                </Link>
                <Link
                  to="/privacy-policy"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Terms & Conditions
                </Link>
                <Link
                  to="/contact"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Contact
                </Link>
              </div>
              <p className="text-sm text-muted-foreground">
                © 2025 PROduce. Elevating festival production worldwide.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

export default TermsConditions;
